import axios, { AxiosError } from 'axios';
import { useEffect, useRef, useState } from 'react';

import { CORE_API_BASE_URL } from '@/api';
import { SchoolConnectExchangeError, SchoolConnectExchangeResponse } from '../types';

const EXCHANGE_ENDPOINT = '/api/connect/school/exchange';

const coreApiWithoutAuth = axios.create({ baseURL: CORE_API_BASE_URL });

/**
 * exchange API의 결과 상태
 */
export type ExchangeStatus = 'idle' | 'loading' | 'success' | 'error' | 'expired';

export interface ExchangeState {
  status: ExchangeStatus;
  data: SchoolConnectExchangeResponse | null;
  /** 사용자에게 보여줄 에러 메시지 (status === 'error' 또는 'expired' 일 때 유효) */
  errorMessage: string | null;
  /** 만료(expired)인지 일반 에러인지 식별을 위해 보관 */
  errorCode: string | null;
}

const INITIAL_STATE: ExchangeState = {
  status: 'idle',
  data: null,
  errorMessage: null,
  errorCode: null
};

export interface UseExchangeAuthCodeOptions {
  /**
   * 첫 응답을 캐시에서 받아 곧바로 success 상태로 시작한다.
   * Resume 흐름에서 동일 authCode 로 exchange API를 재호출하면 410 이 되므로
   * 캐시 hit 시에는 API 호출 자체를 건너뛴다.
   */
  seededData?: SchoolConnectExchangeResponse | null;
}

/**
 * 학교 연동 authCode를 exchange API에 던져 사용자 정보를 받아온다.
 *
 * - authCode가 null/유효하지 않으면 호출하지 않고 idle 상태를 유지한다.
 * - 동일 authCode에 대해 두 번 호출하지 않는다 (StrictMode 중복 방지).
 * - `seededData` 가 주어지면 API 호출 없이 곧바로 success 상태로 진입한다.
 * - skipGlobalErrorToast 옵션을 사용하여 페이지 자체적으로 에러를 표시한다.
 * - 410 / `AuthCodeExpiredOrUsed` 는 `expired` 상태로 분리한다.
 */
export function useExchangeAuthCode(
  authCode: string | null,
  options: UseExchangeAuthCodeOptions = {}
): ExchangeState {
  const { seededData = null } = options;

  const [state, setState] = useState<ExchangeState>(() =>
    seededData
      ? {
          status: 'success',
          data: seededData,
          errorMessage: null,
          errorCode: null
        }
      : INITIAL_STATE
  );
  const startedRef = useRef<string | null>(seededData && authCode ? authCode : null);

  useEffect(() => {
    if (!authCode) {
      setState(INITIAL_STATE);
      return;
    }

    if (startedRef.current === authCode) return;
    startedRef.current = authCode;

    setState({ status: 'loading', data: null, errorMessage: null, errorCode: null });

    coreApiWithoutAuth
      .post<SchoolConnectExchangeResponse>(
        EXCHANGE_ENDPOINT,
        { authCode },
        { skipGlobalErrorToast: true }
      )
      .then((response) => {
        setState({
          status: 'success',
          data: response.data,
          errorMessage: null,
          errorCode: null
        });
      })
      .catch((error: AxiosError<SchoolConnectExchangeError>) => {
        const errorCode = error.response?.data?.error ?? null;
        const status = error.response?.status;

        const isExpired = status === 410 || errorCode === 'AuthCodeExpiredOrUsed';

        setState({
          status: isExpired ? 'expired' : 'error',
          data: null,
          errorMessage: resolveExchangeErrorMessage(error),
          errorCode
        });
      });
  }, [authCode]);

  return state;
}

function resolveExchangeErrorMessage(error: AxiosError<SchoolConnectExchangeError>) {
  const status = error.response?.status;
  const errorCode = error.response?.data?.error;

  if (status === 400 || errorCode === 'InvalidAuthCode') return '유효하지 않은 연동 링크';
  if (
    status === 401 ||
    errorCode === 'SchoolAuthenticationFailed' ||
    errorCode === 'MissingConfiguration'
  ) {
    return '연동 시스템 오류, 관리자에게 문의해 주세요';
  }
  if (status === 404 || errorCode === 'AuthCodeNotFound') return '유효하지 않은 연동 코드';
  if (status === 409 || errorCode === 'AuthCodeConflict') return '다시 시도해 주세요';
  if (status === 410 || errorCode === 'AuthCodeExpiredOrUsed') {
    return '연동 코드가 만료되었거나 이미 사용되었습니다. 다시 발급해 주세요';
  }

  return '연동 처리 중 오류가 발생했습니다. 잠시 후 다시 시도해 주세요';
}
